<template>
  <div class="container public">
    <page-header />
    <div class="content-container">
      <div class="apitokens-section">
        <h2 class="section-title">{{ $t('apiTokensPage.title') }}</h2>
        <p class="text-muted">{{ $t('apiTokensPage.intro') }}</p>

        <!-- One-time cleartext display after creation -->
        <div v-if="createdToken" class="alert alert-success created-token-alert">
          <strong>{{ $t('apiTokensPage.createdHeadline') }}</strong>
          <p class="mb-1">{{ $t('apiTokensPage.createdHint') }}</p>
          <div class="d-flex align-items-center">
            <code class="created-token">{{ createdToken }}</code>
            <button type="button" class="btn btn-sm btn-outline-secondary ms-2" @click="copyToken">
              <font-awesome-icon icon="clone" /> {{ copied ? $t('apiTokensPage.copied') : $t('apiTokensPage.copy') }}
            </button>
            <button type="button" class="btn btn-sm btn-outline-secondary ms-2" @click="createdToken = ''">
              {{ $t('apiTokensPage.dismissToken') }}
            </button>
          </div>
        </div>

        <!-- Create form -->
        <form class="create-form" @submit.prevent="create">
          <div class="row g-2 align-items-end">
            <div class="col-auto">
              <label class="form-label" for="tokenName">{{ $t('apiTokensPage.formName') }}</label>
              <input id="tokenName" type="text" class="form-control" v-model.trim="form.name"
                     :placeholder="$t('apiTokensPage.formNamePlaceholder')" required>
            </div>
            <div class="col-auto">
              <label class="form-label" for="tokenOrganization">{{ $t('apiTokensPage.formOrganization') }}</label>
              <select id="tokenOrganization" class="form-select" v-model="form.organizationId" required>
                <option disabled value="">{{ $t('apiTokensPage.formOrganizationPlaceholder') }}</option>
                <option v-for="organization in organizations" :key="organization.id" :value="organization.id">
                  {{ organization.name }} ({{ organization.code }})
                </option>
              </select>
            </div>
            <div class="col-auto">
              <label class="form-label" for="tokenScope">{{ $t('apiTokensPage.formScope') }}</label>
              <select id="tokenScope" class="form-select" v-model="form.scope" required>
                <option value="totals">{{ $t('apiTokensPage.scopeTotals') }}</option>
                <option value="massnahmenpilot">{{ $t('apiTokensPage.scopeMassnahmenpilot') }}</option>
              </select>
            </div>
            <div class="col-auto">
              <label class="form-label" for="tokenExpiresAt">{{ $t('apiTokensPage.formExpiresAt') }}</label>
              <input id="tokenExpiresAt" type="date" class="form-control" v-model="form.expiresAt">
            </div>
            <div class="col-auto">
              <button type="submit" class="btn btn-primary" :disabled="isCreating || !form.name || !form.organizationId">
                <span v-if="isCreating" class="spinner-border spinner-border-sm"></span>
                <font-awesome-icon v-else icon="plus" />
                &nbsp;{{ $t('apiTokensPage.createButton') }}
              </button>
            </div>
          </div>
        </form>

        <error-row @clearError="clearError" :errorMessage="errorMessage" />

        <div v-show="isLoading" class="is-loading-section">
          <button class="btn btn-primary"><span class="spinner-border spinner-border-sm"></span></button><span> {{ $t('apiTokensPage.isLoading') }} </span>
        </div>
        <div v-show="!isLoading && tokens.length === 0" id="no-data-section">
          {{ $t('apiTokensPage.tableHasNoEntries') }}
        </div>
        <div v-show="!isLoading && tokens.length > 0" id="table-section">
          <table class="table table-hover">
            <thead>
              <tr>
                <th>{{ $t('apiTokensPage.columnName') }}</th>
                <th>{{ $t('apiTokensPage.columnOrganization') }}</th>
                <th>{{ $t('apiTokensPage.columnScope') }}</th>
                <th>{{ $t('apiTokensPage.columnCreated') }}</th>
                <th>{{ $t('apiTokensPage.columnExpiresAt') }}</th>
                <th>{{ $t('apiTokensPage.columnLastUsed') }}</th>
                <th>{{ $t('apiTokensPage.columnStatus') }}</th>
                <th>&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="token in tokens" :key="token.id" :class="{ 'table-secondary text-muted': !token.active }">
                <td>{{ token.name }}</td>
                <td>{{ token.organization }}</td>
                <td>{{ token.scope }}</td>
                <td>{{ formatDateTime(token.created) }}</td>
                <td>{{ token.expiresat ? formatDate(token.expiresat) : '—' }}</td>
                <td>{{ token.lastused ? formatDateTime(token.lastused) : $t('apiTokensPage.neverUsed') }}</td>
                <td>
                  <span v-if="token.active && isExpired(token)" class="badge bg-warning text-dark">{{ $t('apiTokensPage.statusExpired') }}</span>
                  <span v-else-if="token.active" class="badge bg-success">{{ $t('apiTokensPage.statusActive') }}</span>
                  <span v-else class="badge bg-secondary">{{ $t('apiTokensPage.statusRevoked') }}</span>
                </td>
                <td>
                  <button
                    v-if="token.active"
                    type="button"
                    class="btn btn-sm btn-outline-secondary"
                    @click="revoke(token)"
                    :title="$t('apiTokensPage.revoke')"
                  >
                    <font-awesome-icon icon="trash" /> {{ $t('apiTokensPage.revoke') }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <page-footer />
  </div>
</template>

<script>
import moment from 'moment'
import PageHeader from '@/components/PageHeader.vue'
import ErrorRow from '@/components/ErrorRow.vue'
import PageFooter from '@/components/PageFooter.vue'
import PageViewMixin from '@/mixins/PageViewMixin.js'
import apiTokensService from '@/services/apitokens'

import { mapGetters } from 'vuex'

export default {
  name: 'ApiTokensPage',
  mixins: [PageViewMixin],
  components: {
    'page-header': PageHeader,
    'page-footer': PageFooter,
    'error-row': ErrorRow
  },
  data () {
    return {
      tokens: [],
      isLoading: false,
      isCreating: false,
      initialized: false,
      errorMessage: '',
      createdToken: '',
      copied: false,
      form: { name: '', organizationId: '', expiresAt: '', scope: 'totals' }
    }
  },
  computed: {
    ...mapGetters(['client', 'organizations']),
    isAdmin () {
      return this.client && this.client.role === 4
    }
  },
  mounted () {
    this.evaluateAccess()
  },
  watch: {
    // On a direct page load the client data arrives asynchronously
    // (PageHeader dispatches getClientData) — evaluate again once it's there.
    'client.authenticated' () {
      this.evaluateAccess()
    }
  },
  methods: {
    evaluateAccess () {
      if (!this.client || !this.client.authenticated) {
        // still loading; the 401 interceptor routes logged-out users to /login
        return
      }
      if (!this.isAdmin) {
        this.$router.replace({ name: 'home' })
        return
      }
      if (!this.initialized) {
        this.initialized = true
        this.$store.dispatch('getOrganizationData')
        this.loadTokens()
      }
    },
    loadTokens () {
      this.isLoading = true
      this.errorMessage = ''
      apiTokensService.listApiTokens()
        .then((data) => {
          this.tokens = (data && data.apitokens) ? data.apitokens : []
        })
        .catch((error) => {
          this.errorMessage = this.localizedBackendMessage(error.message)
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    create () {
      this.isCreating = true
      this.errorMessage = ''
      this.createdToken = ''
      this.copied = false
      const payload = {
        organizationId: this.form.organizationId,
        name: this.form.name,
        expiresAt: this.form.expiresAt || null,
        scope: this.form.scope
      }
      apiTokensService.createApiToken(payload)
        .then((data) => {
          this.createdToken = data.token
          this.form = { name: '', organizationId: '', expiresAt: '', scope: 'totals' }
          this.loadTokens()
        })
        .catch((error) => {
          this.errorMessage = this.localizedBackendMessage(error.message)
        })
        .finally(() => {
          this.isCreating = false
        })
    },
    revoke (token) {
      if (!confirm(this.$t('apiTokensPage.revokeConfirm', { name: token.name }))) {
        return
      }
      apiTokensService.revokeApiToken(token.id)
        .then(() => {
          this.loadTokens()
        })
        .catch((error) => {
          this.errorMessage = this.localizedBackendMessage(error.message)
        })
    },
    copyToken () {
      navigator.clipboard.writeText(this.createdToken)
        .then(() => {
          this.copied = true
        })
        .catch(() => {
          this.copied = false
        })
    },
    isExpired (token) {
      return token.expiresat && moment(token.expiresat).isBefore(moment())
    },
    formatDateTime (value) {
      return value ? moment(value).format('YYYY-MM-DD HH:mm') : ''
    },
    formatDate (value) {
      return value ? moment(value).format('YYYY-MM-DD') : ''
    },
    clearError () {
      this.errorMessage = ''
    }
  }
}
</script>

<style lang="scss" scoped>
.apitokens-section {
  .create-form {
    margin-bottom: 1.5rem;
  }

  .created-token-alert {
    .created-token {
      user-select: all;
      font-size: 1rem;
      padding: 0.25rem 0.5rem;
      background: rgba(0, 0, 0, 0.06);
      border-radius: 4px;
    }
  }
}
</style>
